import React, { Component, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import memoize from 'memoizee';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { Link, Route, Switch, withRouter } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import { Helmet } from 'react-helmet';
import { HTTP_STATUS_NOT_FOUND } from 'cityads-v3/utils/apiUtils'
import { leftMenuLargeWidth } from 'cityads-v3/utils/styleUtils';
import { mindboxNotifyAddProductToList, mindboxNotifyViewProduct, yandexReachGoal, amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';

import {
  getConnectRequests,
  getOfferById,
  getOfferStat,
  getOfferTags,
} from 'cityads-v3/actions/offers';

import {
  getSubscriptionStatuses
} from 'cityads-v3/actions/subscriptionStatuses';

import classes from './OfferPage.scss';

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import ActionTypeDirection from 'cityads-v3/components/Format/ActionTypeDirection';
import AvatarWithFallback from 'cityads-v3/components/ImgWithFallback/AvatarWithFallback';
import Balaclava from 'cityads-v3/components/Balaclava';
import Category from 'cityads-v3/components/Format/Category';
import { FORMAT_CURRENCY } from 'cityads-v3/components/Format/NumberFormat';
import DateFormat from 'cityads-v3/components/Format/DateFormat';
import OfferOptions from 'cityads-v3/components/OfferOptions/OfferOptions';
import ScrollToTopOnMount from 'cityads-v3/containers/misc/ScrollToTopOnMount';
import SnackbarButton from 'cityads-v3/components/Buttons/SnackbarButton';
import TargetValueFormat from 'cityads-v3/components/Format/TargetValueFormat'
import TemporaryRedirect from 'cityads-v3/components/Router/TemporaryRedirect';
import NotFound from 'cityads-v3/containers/misc/NotFound';

import Reward from './Reward';
import Terms from './Terms';
import Tools from './Tools';
import Traffic from './Traffic';
import Description from './Description';
import News from 'cityads-v3/containers/webmaster/news/News';
import CurrentNewsItem from 'cityads-v3/containers/webmaster/news/CurrentNewsItem';
import GetLinkDialog from '../offers/GetLinkDialog';
import GetLinkButton from './GetLinkButton';

import withHooks from 'cityads-v3/decorators/withHooks';
import { useOfferInfoSender } from 'cityads-v3/hooks/amplitude';
import { useOfferHasRewardCalculator, useOfferTags } from 'cityads-v3/hooks/offers';

import {
  ACTION_TYPE_DIRECTION_ID_API,
  SUBSCRIPTION_STATUS_ID_PENDING,
} from 'cityads-v3/reducers/offers';

const ADAPTIVE_FULLPAGE_WIDTH = 1100;

const MAX_URL_LENGTH = 44;

const indicatorsEffective = (t, { isApi, crIsExpected, leadbasedIsExpected }) => ([
  isApi ? false : {
    title: crIsExpected ? t('table.crExpected') : t('table.cr'),
    name: 'cr',
    format: 'percent',
    precision: 2,
    tooltip: crIsExpected ? t('table.crExpectedTooltip') : t('table.crTooltip'),
    formatter: value => value === null ? null : value * 100
  },
  {
    title: leadbasedIsExpected ? t('table.arExpected') : t('table.ar'),
    name: 'ar',
    format: 'percent',
    precision: 0,
    tooltip: leadbasedIsExpected ? t('table.arExpectedTooltip') : t('table.arTooltip'),
    formatter: value => value !== null && value !== 0 ? value * 100 : null
  },
  isApi ? {
    title: leadbasedIsExpected ? t('table.ecplExpected') : t('table.ecpl'),
    name: 'ecpl',
    tooltip: leadbasedIsExpected ? t('table.ecplExpectedTooltip') : t('table.ecplTooltip'),
    formatter: (value, offerInfo, { ecpl, currency_code, original_ecpl, original_currency_code }) => (
      <TargetValueFormat
        format={FORMAT_CURRENCY}
        currency={currency_code}
        min={ecpl}
        max={ecpl}
        originalCurrency={original_currency_code}
        originalMin={original_ecpl}
        originalMax={original_ecpl}
      />
    ),
  } : false,
  isApi ? false : {
    title: leadbasedIsExpected ? t('table.ecpcExpected') : t('table.ecpc'),
    name: 'ecpc',
    tooltip: leadbasedIsExpected ? t('table.ecpcExpectedTooltip') : t('table.ecpcTooltip'),
    formatter: (value, offerInfo, { ecpc, currency_code, original_ecpc, original_currency_code }) => (
      <TargetValueFormat
        format={FORMAT_CURRENCY}
        currency={currency_code}
        min={ecpc}
        max={ecpc}
        originalCurrency={original_currency_code}
        originalMin={original_ecpc}
        originalMax={original_ecpc}
      />
    ),
  }
]);

const indicatorsInfo = (t) => ([
  {
    title: t('table.approval_time'),
    name: 'approval_time',
    format: 'number',
    precision: 0,
    tooltip: t('table.approval_timeTooltip'),
    suffix: t('table.approval_time_suffix')
  },
  {
    title: t('table.cookie_lifetime'),
    name: 'cookie_lifetime',
    format: 'number',
    precision: 0,
    tooltip: t('table.cookie_lifetimeTooltip'),
    suffix: t('table.cookie_lifetime_suffix')
  },
]);

const getOfferTargetTypes = memoize(function getOfferTargetTypes(targetsByOfferId, offerId) {
  return [ ...(targetsByOfferId[offerId] || []).reduce((acc, target) => acc.add(target.type.split('_')[0]), new Set()) ]
});

@withRouter
@connect(
  (state, props) => {
    const id = Number(props.match.params.id);
    return {
      pageId: id,
      offer: state.offers.offersById[id] || {},
      isLoading: state.offers.isLoadingOfferById[id] || null,
      loadingStatus: state.offers.offerLoadingStatusById[id] || null,
      offerStat: state.offers.offerStatByOfferId[id] || {},
      offerTargetTypes: getOfferTargetTypes(state.offers.targetsByOfferId, id),
      language: state.language.language,
      subscriptionStatusesById: state.offers.subscription_status_byId,
      backendTimeout: state.app.backendTimeout,
      isLoggedIn: state.auth.isLoggedIn,
      isMenuDocked: state.menu.docked,
    }
  }
)
@withHooks(props => {
  const { offer } = props;

  const sendOfferInfo = useOfferInfoSender(offer?.id);
  const hasRewardCalc = useOfferHasRewardCalculator(offer?.id);
  const [, offerTagsByCode] = useOfferTags();

  useEffect(() => {
    sendOfferInfo('d-v1-e8', 'offer_view');
  }, [sendOfferInfo]);

  return {
    ...props,
    hasRewardCalc,
    offerTagsByCode,
  };
})
@withTranslation(['Offers'], { i18n, wait: false })
export default class OfferPage extends Component {

  static propTypes = {
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    pageId: PropTypes.number,
    offer: PropTypes.object,
    offerTagsByCode: PropTypes.object,
    loadingStatus: PropTypes.number,
    offerStat: PropTypes.object,
    isMenuDocked: PropTypes.bool,
  }

  UNSAFE_componentWillMount() {
    const {
      dispatch,
      offer,
      isLoading,
      loadingStatus,
      pageId,
      backendTimeout
    } = this.props;

    if (isNaN(pageId)) {
      console.error('Try to load offerPage with NaN id');
      return;
    }

    if (typeof window === 'undefined' && backendTimeout) {
      console.error('OfferPage: backend timeout');
      return;
    }

    if (!offer.id && !isLoading && !loadingStatus) {
      dispatch(getOfferById(pageId));
    }

  }

  componentDidMount() {
    const {
      dispatch,
      pageId,
      isLoggedIn,
      offerStat,
      offerTagsByCode,
      subscriptionStatusesById,
    } = this.props;

    if (typeof offerStat.cr === 'undefined') {
      dispatch( getOfferStat([pageId]) );
    }

    if (!Object.getOwnPropertyNames(subscriptionStatusesById).length) {
      dispatch(getSubscriptionStatuses());
    }

    if (!Object.getOwnPropertyNames(offerTagsByCode).length) {
      dispatch(getOfferTags());
    }

    if (isLoggedIn) {
      dispatch(getConnectRequests([pageId]));
    }

    dispatch(mindboxNotifyViewProduct('Online.OfferMore', pageId));
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const oldProps = this.props;
    const { dispatch, language, pageId, isLoggedIn } = newProps;
    if(isNaN(pageId)) {
      return;
    }

    if (oldProps.pageId !== pageId || oldProps.language !== language) {
      dispatch(getOfferById(pageId));
    }

    if ((!oldProps.isLoggedIn && isLoggedIn) || oldProps.language !== language) {
      dispatch(getSubscriptionStatuses());
      dispatch(getOfferTags());
    }

    if (!oldProps.isLoggedIn && isLoggedIn) {
      dispatch(getConnectRequests([pageId]));
    }
  }

  componentDidUpdate(prevProps) {
    const { dispatch, pageId } = this.props;

    if (pageId !== prevProps.pageId) {
      dispatch(mindboxNotifyViewProduct('Online.OfferMore', pageId));
    }
  }

  getLogoSrcs() {
    const { offer } = this.props;
    const logo = offer.icons?.favicon_svg?.path;

    return {
      src: logo,
      fallbackSrc: [
        offer.icons?.favicon_orig?.path,
        require('../offers/img/default_avatar.svg')
      ]
    };
  }

  onConnectClick = () => {
    const { dispatch, offer } = this.props;
    dispatch(mindboxNotifyAddProductToList('Online.GetLink', offer.id));
  }

  onRewardTabClick = (offerId) => {
    const eventData = { offerId };
    yandexReachGoal('REWARD_OFFER_TAB_CLICK', eventData);
    amplitudeSendEvent('makeActions', 'd-v2-e9', 'offers', 'REWARD_OFFER_TAB_CLICK', eventData);
  }

  getBacklinkProps() {
    const { location, t } = this.props;
    const source = location.state?.source;
    const search = location.state?.sourceSearch;
    const sourceName = source?.name ?? source;

    switch (sourceName) {
      default:
        return {
          backlink: {
            pathname: '/publisher/offers',
            search,
          },
          backlinkText: t('offersList'),
        };

      case 'coupons':
        return {
          backlink: {
            pathname: '/publisher/coupons',
            search,
          },
          backlinkText: t('couponsList'),
        };

      case 'widgets':
        return {
          backlink: {
            pathname: '/publisher/tools/widgets',
            search,
          },
          backlinkText: t('Widgets:widgetList'),
        };
      case 'create_widget':
        return {
          backlink: {
            pathname: '/publisher/tools/widgets/create',
            search,
          },
          backlinkText: t('Widgets:pageTitle'),
        };

      case 'create_backurl':
        return {
          backlink: {
            pathname: source.url,
            search,
          },
          backlinkText: t('Widgets:pageTitle'),
        };

      case 'firano':
        return {
          backlink: {
            pathname: '/publisher/firano',
            search,
          },
          backlinkText: t('Finance:title'),
        };

      case 'google_ads_integration':
        return {
          backlink: {
            pathname: '/publisher/tools/googleAdsIntegration',
            search,
          },
          backlinkText: t('GoogleAdsIntegration:list.title'),
        };
      case 'create_google_ads_integration':
        return {
          backlink: {
            pathname: source.url,
            search,
          },
          backlinkText: t('GoogleAdsIntegration:create.title'),
        };

      case 'requests_history':
        return {
          backlink: {
            pathname: '/publisher/requestsHistory',
            search,
          },
          backlinkText: t('Offers:requestsHistory.title'),
        };
    }
  }

  render() {
    const {
      isLoggedIn,
      pageId,
      offer,
      loadingStatus,
      offerStat,
      offerTargetTypes,
      offerTagsByCode,
      subscriptionStatusesById,
      location,
      match,
      t,
      isMenuDocked,
      hasRewardCalc,
    } = this.props;

    const backlinkProps = this.getBacklinkProps();

    if (loadingStatus === HTTP_STATUS_NOT_FOUND || isNaN(pageId)) {
      return (
        <NotFound
          link     = {backlinkProps.backlink}
          linkText = {backlinkProps.backlinkText}
        />
      );
    }

    if (!offer.id) {
      return (
        <div className={classNames(classes.container, classes.loading)}>
          <Balaclava
            {...backlinkProps}
          />

          <CircularProgress />
        </div>
      );
    }

    const tabValue = match.params.tab === 'news' ? null : match.params.tab;
    const { cr_is_expected, leadbased_is_expected } = offerStat;
    const offerLogoSrcs = this.getLogoSrcs();

    return (
      <div className={classes.container}>
        <ScrollToTopOnMount/>

        <Helmet>
          <title>{offer.translated_name || offer.name}</title>
        </Helmet>
        <Balaclava
          title = {offer.translated_name || offer.name}
          {...backlinkProps}
        />

        <Card className={classes.mainInfo}>
          <CardContent className={classes.mainInfo__content}>

            <div className={classes.mainInfo__left}>
              <AvatarWithFallback
                src={offerLogoSrcs.src}
                useImg={true}
                fallbackSrc={offerLogoSrcs.fallbackSrc}
                alt={offer.translated_name || offer.name}
                classes={{ root: classes.offerAvatar }}
              />

              <GetLinkButton
                buttonClasses={{
                  root: classes.mainInfo__button_root,
                  label: classes.mainInfo__button_label
                }}
                className={classes.mainInfo__button}
                id = {offer.id}
                alreadyRequested = {
                  <div className={classes.mainInfo__alreadyRequestedText}>
                    <SnackbarButton
                      color="info"
                      variant = "contained"
                      message = {t('alreadyRequestedText')}
                    >{t('exports.getActionLink')}</SnackbarButton>
                    <div className={classes.mainInfo__alreadyRequestedTextDesc}>{t('alreadyRequestedText')}</div>
                  </div>
                }
                onClick={this.onConnectClick}
                getActionLinkUrl={`/publisher/offer/${offer.id}/getActionLink/`}
              />
            </div>
            <div className={classes.mainInfo__right}>
              <Typography
                component="div"
                color="text.primary"
                className={classes.mainInfo__title}
              >
                {offer.translated_name || offer.name}
              </Typography>

              {offer.tags.map(tagCode => offerTagsByCode[tagCode])
                .filter(tag => tag?.isVisible)
                .map(tag => (
                  <div key={tag.code}><Typography component="span" variant="body2">{tag.translated_name || tag.name}</Typography></div>
                ))
              }

              {(isLoggedIn || offer.subscription_status === SUBSCRIPTION_STATUS_ID_PENDING) ?
                (
                  <>
                    <NameValueText
                      name={t('status')}
                      value={subscriptionStatusesById[offer.subscription_status]?.status_name}
                    />

                    {(offer.subscription_status === SUBSCRIPTION_STATUS_ID_PENDING) && (
                      <NameValueText
                        name={t('table.date_planned_start')}
                        value={<DateFormat date={offer.planned_start_date} />}
                      />
                    )}
                  </>
                ) :
                false
              }

              <NameValueText
                name="Offer ID"
                value={offer.id}
              />

              <NameValueText
                name={t('siteUrl')}
                value={
                  <a href={offer.site_url} target="_blank" rel="noopener noreferrer">
                    {offer.site_url.substr(0, MAX_URL_LENGTH) + (offer.site_url.length > MAX_URL_LENGTH ? '…' : '')}
                    &nbsp;
                    <OpenInNewOutlinedIcon className={classes.siteUrlIcon} />
                  </a>
                }
              />

              <NameValueText
                name={t('offerTargetTypes')}
                value={offerTargetTypes.join(', ')}
              />

              <Category id={offer.business_category}>
                {category => (
                  <NameValueText name={t('category')} value={category} />
                )}
              </Category>

              <NameValueText
                name={t('filters.offer_type')}
                value={<ActionTypeDirection id={offer.action_type_direction_id} />}
              />
            </div>

            <GetLinkDialog regexpGetActionLink={/(\/publisher\/offer\/([0-9]{1,}))\/getActionLink/g}/>

          </CardContent>
        </Card>


        <Card className={classes.indicators}>
          <CardContent>
            <div>
              <OfferOptions
                className={classes.indicators__item}
                title={`${t('offerEfficiency')}:`}
                options={indicatorsEffective(t, {
                  isApi: Number(offer.action_type_direction_id) === ACTION_TYPE_DIRECTION_ID_API,
                  crIsExpected: cr_is_expected,
                  leadbasedIsExpected: leadbased_is_expected,
                })}
                offerInfo={offer}
                offerStat={offerStat}
                align="left"
              />
              <OfferOptions
                title={`${t('offerFeature')}:`}
                options={indicatorsInfo(t)}
                offerInfo={offer}
                offerStat={offer.offerStat}
                align="left"
              />
            </div>
          </CardContent>
        </Card>


        <Card className={classes.content}>
          <CardContent classes={{root: classes.content__content}}>
            <MediaQuery key="start" maxWidth={ADAPTIVE_FULLPAGE_WIDTH + (isMenuDocked ? parseInt(leftMenuLargeWidth, 10) : 0)}>
              {matches => (
                <Tabs
                  classes={{ root: classes.content__tabs }}
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabValue || 'terms'}
                  variant={matches ? 'scrollable' : 'fullWidth'}
                >
                  <Tab
                    classes   = {{root: classes.content__tab}}
                    value     = "terms"
                    label     = {t('page.terms')}
                    component = {Link}
                    to        = {{ ...location, pathname: `/publisher/offer/${offer.id}` }}
                  />
                  <Tab
                    classes   = {{root: classes.content__tab}}
                    value     = "traffic"
                    label     = {t('page.traffic')}
                    component = {Link}
                    to        = {{ ...location, pathname: `/publisher/offer/${offer.id}/traffic` }}
                  />
                  <Tab
                    classes   = {{root: classes.content__tab}}
                    value     = "description"
                    label     = {t('page.description')}
                    component = {Link}
                    to        = {{ ...location, pathname: `/publisher/offer/${offer.id}/description` }}
                  />
                  <Tab
                    classes   = {{root: classes.content__tab}}
                    value     = "tools"
                    label     = {t('page.tools')}
                    component = {Link}
                    to        = {{ ...location, pathname: `/publisher/offer/${offer.id}/tools` }}
                  />
                  {hasRewardCalc &&
                    <Tab
                      classes   = {{root: classes.content__tab}}
                      value     = "reward"
                      label     = {t('page.reward')}
                      component = {Link}
                      to        = {{ ...location, pathname: `/publisher/offer/${offer.id}/reward` }}
                      onClick   = {this.onRewardTabClick.bind(this, offer.id)}
                    />
                  }
                  {matches && <Tab
                    classes   = {{root: classes.content__tab}}
                    value     = "news-list"
                    label     = {t('page.news')}
                    component = {Link}
                    to        = {{ ...location, pathname: `/publisher/offer/${offer.id}/news-list` }}
                  />}
                </Tabs>
              )}
            </MediaQuery>

            <Switch>

              <Route path="/publisher/offer/:id/traffic">
                <Traffic
                  offerId={offer.id}
                  actionTypeDirectionId={offer.action_type_direction_id}
                  categoryId={offer.business_category}
                />
              </Route>

              <Route path="/publisher/offer/:id/description">
                <Description
                  ids={offer.descriptions}
                />
              </Route>

              <Route path="/publisher/offer/:id/tools">
                <Tools
                  offerId                 = {offer.id}
                  offerToolIds            = {offer.tools}
                  offerSubscriptionStatus = {offer.subscription_status}
                />
              </Route>

              <Route path="/publisher/offer/:id/reward">
                <Reward
                  offerId = {offer.id}
                />
              </Route>

              <Route path="/publisher/offer/:id/news-list">
                <MediaQuery key="start" maxWidth={ADAPTIVE_FULLPAGE_WIDTH + (isMenuDocked ? parseInt(leftMenuLargeWidth, 10) : 0)}>
                  {matches => matches ?
                    (
                      <>
                        <News
                          className    = {classes.newsTab}
                          offerId      = {pageId}
                          emptyMessage = {t('page.newsEmpty')}
                        />
                        {isLoggedIn ? (
                          <Button color="primary" href={`/wm/news?news_keyword&action_id[]=${encodeURIComponent(offer.id)}`}>{t('page.all_news')}</Button>
                        ) : false}
                      </>
                    ) : (
                      <TemporaryRedirect to={{ ...location, pathname: `/publisher/offer/${pageId}` }} />
                    )
                  }
                </MediaQuery>
              </Route>

              <Route path="/publisher/offer/:id/">
                <Terms
                  offer={offer}
                />
              </Route>

            </Switch>

          </CardContent>
        </Card>

        <MediaQuery key="start" minWidth={ADAPTIVE_FULLPAGE_WIDTH + (isMenuDocked ? parseInt(leftMenuLargeWidth, 10) : 0) + 1}>
          {matches => matches ? (
            <Card className={classes.news}>
              <CardContent>
                <News
                  title        = {t('page.offer_news')}
                  offerId      = {pageId}
                  emptyMessage = {t('page.newsEmpty')}
                />
              </CardContent>
              <CardActions>
                {isLoggedIn ? (
                  <Button color="primary" href={`/wm/news?news_keyword&action_id[]=${encodeURIComponent(offer.id)}`}>{t('page.all_news')}</Button>
                ) : false}
              </CardActions>
            </Card>
          ) : false}
        </MediaQuery>

        <Route
          path="/publisher/offer/*/news/:newsId"
          children={({ match }) => {
            const subPath = match ? match.params[0] : '';
            const newsId = match ? parseInt(match.params.newsId, 10) : null;

            return (
              <CurrentNewsItem
                id={newsId}
                open={Boolean(match)}
                baseRoute={{ ...location, pathname: `/publisher/offer/${subPath}` }}
                canonicalBaseRoute={`/publisher/offer/${pageId}/news`}
              />
            );
          }}
        />
      </div>
    )
  }
}


function NameValueText({ name, value }) {
  return (
    <Typography variant="body2" color="textSecondary">
      {name}:{' '}
      <Typography color="textPrimary" variant="body2" component="span">
        {value}
      </Typography>
    </Typography>
  )
}
